/**
 * Soul Fruit — Gran Canaria sunset planner
 * Requires SunCalc (suncalc.js) loaded first.
 */
(() => {
  const TZ = 'Atlantic/Canary';
  const DEFAULT_LAT = 27.76;
  const DEFAULT_LNG = -15.58;
  const ARRIVE_BEFORE_MS = 20 * 60 * 1000;
  const FULL_MOON_BAND = 0.03;
  const NEW_MOON_BAND = 0.03;

  const PHASE_NAMES = [
    [0.0625, 'New moon'],
    [0.1875, 'Waxing crescent'],
    [0.3125, 'First quarter'],
    [0.4375, 'Waxing gibbous'],
    [0.5625, 'Full moon'],
    [0.6875, 'Waning gibbous'],
    [0.8125, 'Last quarter'],
    [0.9375, 'Waning crescent'],
    [1.0001, 'New moon'],
  ];

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function formatTime(date, locale) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
    try {
      return new Intl.DateTimeFormat(locale || undefined, {
        timeZone: TZ,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    } catch {
      return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
  }

  function formatDayLabel(date, locale, opts) {
    try {
      return new Intl.DateTimeFormat(locale || undefined, {
        timeZone: TZ,
        ...opts,
      }).format(date);
    } catch {
      return date.toISOString().slice(0, 10);
    }
  }

  function ymdInTz(date) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  }

  function noonOnYmd(ymd) {
    // Midday UTC-ish for Canary (UTC+0/+1) keeps getTimes on the right calendar day.
    return new Date(`${ymd}T12:00:00+00:00`);
  }

  function parseYmd(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const d = noonOnYmd(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  function addDays(ymd, delta) {
    const d = noonOnYmd(ymd);
    d.setUTCDate(d.getUTCDate() + delta);
    return ymdInTz(d);
  }

  function startOfWeekMonday(ymd) {
    const d = noonOnYmd(ymd);
    // Get weekday in Canary
    const wd = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short' }).format(d);
    const map = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
    const offset = map[wd] ?? 0;
    return addDays(ymd, -offset);
  }

  function monthMeta(ymd) {
    const [y, m] = ymd.split('-').map(Number);
    const first = `${y}-${pad(m)}-01`;
    const nextM = m === 12 ? 1 : m + 1;
    const nextY = m === 12 ? y + 1 : y;
    const lastDay = new Date(Date.UTC(nextY, nextM - 1, 0)).getUTCDate();
    return { year: y, month: m, first, daysInMonth: lastDay };
  }

  function phaseName(phase) {
    for (const [max, name] of PHASE_NAMES) {
      if (phase < max) return name;
    }
    return 'New moon';
  }

  function moonMark(phase) {
    if (phase < NEW_MOON_BAND || phase > 1 - NEW_MOON_BAND) return 'new';
    if (Math.abs(phase - 0.5) < FULL_MOON_BAND) return 'full';
    return null;
  }

  function dayLengthLabel(sunrise, sunset) {
    if (!(sunrise instanceof Date) || !(sunset instanceof Date)) return '—';
    const ms = sunset - sunrise;
    if (ms <= 0 || Number.isNaN(ms)) return '—';
    const totalMin = Math.round(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h}h ${pad(m)}m`;
  }

  function timesFor(ymd, lat, lng) {
    const date = noonOnYmd(ymd);
    const times = SunCalc.getTimes(date, lat, lng);
    const moon = SunCalc.getMoonIllumination(date);
    const arriveBy = times.sunset instanceof Date && !Number.isNaN(times.sunset)
      ? new Date(times.sunset.getTime() - ARRIVE_BEFORE_MS)
      : null;
    return {
      ymd,
      sunrise: times.sunrise,
      sunset: times.sunset,
      goldenHour: times.goldenHour,
      blueHourEnd: times.dusk,
      arriveBy,
      dayLength: dayLengthLabel(times.sunrise, times.sunset),
      moonFraction: moon.fraction,
      moonPhase: moon.phase,
      moonName: phaseName(moon.phase),
      moonMark: moonMark(moon.phase),
    };
  }

  function setUrlDate(ymd) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('date', ymd);
      window.history.replaceState({}, '', url);
    } catch {
      /* ignore */
    }
  }

  function shareUrl(ymd) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('date', ymd);
      return url.toString();
    } catch {
      return window.location.href;
    }
  }

  class SfSunsetWidget extends HTMLElement {
    connectedCallback() {
      if (typeof SunCalc === 'undefined') {
        this.querySelector('[data-sf-sw-error]')?.removeAttribute('hidden');
        return;
      }

      this.lat = Number(this.dataset.lat) || DEFAULT_LAT;
      this.lng = Number(this.dataset.lng) || DEFAULT_LNG;
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.compact = this.dataset.mode === 'compact';
      this.fullPageUrl = this.dataset.fullPageUrl || '';

      this.view = 'day';
      this.ymd = this.dataset.initialDate || this.readDateParam() || ymdInTz(new Date());

      this.els = {
        date: this.querySelector('[data-sf-sw-date]'),
        tabs: this.querySelectorAll('[data-sf-sw-view]'),
        panels: {
          day: this.querySelector('[data-sf-sw-panel="day"]'),
          week: this.querySelector('[data-sf-sw-panel="week"]'),
          month: this.querySelector('[data-sf-sw-panel="month"]'),
        },
        dayGrid: this.querySelector('[data-sf-sw-day-grid]'),
        weekGrid: this.querySelector('[data-sf-sw-week]'),
        monthGrid: this.querySelector('[data-sf-sw-month]'),
        monthLabel: this.querySelector('[data-sf-sw-month-label]'),
        moon: this.querySelector('[data-sf-sw-moon]'),
        share: this.querySelector('[data-sf-sw-share]'),
        shareStatus: this.querySelector('[data-sf-sw-share-status]'),
        prev: this.querySelector('[data-sf-sw-prev]'),
        next: this.querySelector('[data-sf-sw-next]'),
        today: this.querySelector('[data-sf-sw-today]'),
      };

      this.bind();
      this.render();
    }

    readDateParam() {
      try {
        return new URL(window.location.href).searchParams.get('date');
      } catch {
        return null;
      }
    }

    bind() {
      this.els.date?.addEventListener('change', () => {
        const next = parseYmd(this.els.date.value);
        if (!next) return;
        this.ymd = this.els.date.value;
        this.render();
      });

      this.els.tabs?.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.view = tab.dataset.sfSwView;
          this.render();
        });
      });

      this.els.prev?.addEventListener('click', () => {
        if (this.view === 'month') {
          const { year, month } = monthMeta(this.ymd);
          const pm = month === 1 ? 12 : month - 1;
          const py = month === 1 ? year - 1 : year;
          this.ymd = `${py}-${pad(pm)}-01`;
        } else if (this.view === 'week') {
          this.ymd = addDays(this.ymd, -7);
        } else {
          this.ymd = addDays(this.ymd, -1);
        }
        this.render();
      });

      this.els.next?.addEventListener('click', () => {
        if (this.view === 'month') {
          const { year, month } = monthMeta(this.ymd);
          const nm = month === 12 ? 1 : month + 1;
          const ny = month === 12 ? year + 1 : year;
          this.ymd = `${ny}-${pad(nm)}-01`;
        } else if (this.view === 'week') {
          this.ymd = addDays(this.ymd, 7);
        } else {
          this.ymd = addDays(this.ymd, 1);
        }
        this.render();
      });

      this.els.today?.addEventListener('click', () => {
        this.ymd = ymdInTz(new Date());
        this.render();
      });

      this.els.share?.addEventListener('click', async () => {
        const url = shareUrl(this.ymd);
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
          } else {
            const ta = document.createElement('textarea');
            ta.value = url;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            ta.remove();
          }
          if (this.els.shareStatus) {
            this.els.shareStatus.textContent = this.dataset.copiedLabel || 'Link copied';
            this.els.shareStatus.hidden = false;
            clearTimeout(this._shareTimer);
            this._shareTimer = setTimeout(() => {
              this.els.shareStatus.hidden = true;
            }, 2200);
          }
        } catch {
          window.prompt(this.dataset.copyPrompt || 'Copy this link', url);
        }
      });
    }

    render() {
      if (this.els.date) this.els.date.value = this.ymd;
      setUrlDate(this.ymd);

      this.els.tabs?.forEach((tab) => {
        const active = tab.dataset.sfSwView === this.view;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.classList.toggle('is-active', active);
      });

      Object.entries(this.els.panels).forEach(([key, panel]) => {
        if (!panel) return;
        const show = this.compact ? key === 'day' : key === this.view;
        panel.hidden = !show;
      });

      if (this.compact || this.view === 'day') this.renderDay();
      if (!this.compact && this.view === 'week') this.renderWeek();
      if (!this.compact && this.view === 'month') this.renderMonth();
    }

    metric(label, value, hint) {
      return `
        <div class="sf-sw__metric">
          <dt>${label}</dt>
          <dd>
            <span class="sf-sw__value">${value}</span>
            ${hint ? `<span class="sf-sw__hint">${hint}</span>` : ''}
          </dd>
        </div>
      `;
    }

    renderDay() {
      const t = timesFor(this.ymd, this.lat, this.lng);
      const loc = this.locale;
      if (this.els.dayGrid) {
        this.els.dayGrid.innerHTML = [
          this.metric(this.dataset.labelSunset || 'Sunset', formatTime(t.sunset, loc)),
          this.metric(
            this.dataset.labelArrive || 'Arrive by',
            formatTime(t.arriveBy, loc),
            this.dataset.hintArrive || 'About 20 minutes before sunset'
          ),
          this.metric(
            this.dataset.labelGolden || 'Golden hour',
            formatTime(t.goldenHour, loc),
            this.dataset.hintGolden || 'Soft evening light begins'
          ),
          this.metric(
            this.dataset.labelBlue || 'Blue hour ends',
            formatTime(t.blueHourEnd, loc),
            this.dataset.hintBlue || 'Civil twilight, after the sun dips'
          ),
          this.metric(this.dataset.labelSunrise || 'Sunrise', formatTime(t.sunrise, loc)),
          this.metric(this.dataset.labelDaylength || 'Day length', t.dayLength),
        ].join('');
      }
      if (this.els.moon) {
        const pct = Math.round((t.moonFraction || 0) * 100);
        const mark =
          t.moonMark === 'full'
            ? ` · ${this.dataset.labelFullMoon || 'Full moon'}`
            : t.moonMark === 'new'
              ? ` · ${this.dataset.labelNewMoon || 'New moon'}`
              : '';
        this.els.moon.innerHTML = `
          <span class="sf-sw__moon-phase" data-mark="${t.moonMark || ''}" aria-hidden="true"></span>
          <span class="sf-sw__moon-text">
            <strong>${t.moonName}</strong>
            <span>${pct}% ${this.dataset.labelIllum || 'illuminated'}${mark}</span>
          </span>
        `;
      }
    }

    renderWeek() {
      const start = startOfWeekMonday(this.ymd);
      const days = [];
      for (let i = 0; i < 7; i++) {
        const ymd = addDays(start, i);
        const t = timesFor(ymd, this.lat, this.lng);
        const selected = ymd === this.ymd;
        days.push(`
          <button type="button" class="sf-sw__week-day${selected ? ' is-selected' : ''}" data-ymd="${ymd}">
            <span class="sf-sw__week-name">${formatDayLabel(noonOnYmd(ymd), this.locale, { weekday: 'short' })}</span>
            <span class="sf-sw__week-date">${formatDayLabel(noonOnYmd(ymd), this.locale, { day: 'numeric', month: 'short' })}</span>
            <span class="sf-sw__week-sunset">${formatTime(t.sunset, this.locale)}</span>
            ${t.moonMark ? `<span class="sf-sw__moon-dot" data-mark="${t.moonMark}" title="${t.moonName}"></span>` : ''}
          </button>
        `);
      }
      if (this.els.weekGrid) {
        this.els.weekGrid.innerHTML = days.join('');
        this.els.weekGrid.querySelectorAll('[data-ymd]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.ymd = btn.getAttribute('data-ymd');
            this.view = 'day';
            this.render();
          });
        });
      }
    }

    renderMonth() {
      const { year, month, first, daysInMonth } = monthMeta(this.ymd);
      if (this.els.monthLabel) {
        this.els.monthLabel.textContent = formatDayLabel(noonOnYmd(first), this.locale, {
          month: 'long',
          year: 'numeric',
        });
      }

      const firstWd = new Intl.DateTimeFormat('en-US', {
        timeZone: TZ,
        weekday: 'short',
      }).format(noonOnYmd(first));
      const map = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
      const lead = map[firstWd] ?? 0;

      const cells = [];
      for (let i = 0; i < lead; i++) {
        cells.push('<div class="sf-sw__cell sf-sw__cell--empty"></div>');
      }
      for (let day = 1; day <= daysInMonth; day++) {
        const ymd = `${year}-${pad(month)}-${pad(day)}`;
        const t = timesFor(ymd, this.lat, this.lng);
        const selected = ymd === this.ymd;
        const today = ymd === ymdInTz(new Date());
        cells.push(`
          <button type="button" class="sf-sw__cell${selected ? ' is-selected' : ''}${today ? ' is-today' : ''}" data-ymd="${ymd}">
            <span class="sf-sw__cell-day">${day}</span>
            <span class="sf-sw__cell-sunset">${formatTime(t.sunset, this.locale)}</span>
            ${t.moonMark ? `<span class="sf-sw__moon-dot" data-mark="${t.moonMark}" title="${t.moonName}"></span>` : ''}
          </button>
        `);
      }

      if (this.els.monthGrid) {
        this.els.monthGrid.innerHTML = cells.join('');
        this.els.monthGrid.querySelectorAll('[data-ymd]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.ymd = btn.getAttribute('data-ymd');
            this.view = 'day';
            this.render();
          });
        });
      }
    }
  }

  if (!customElements.get('sf-sunset-widget')) {
    customElements.define('sf-sunset-widget', SfSunsetWidget);
  }
})();
